import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, type RouterHistory } from '@tanstack/react-router';
import { Gate } from './Gate';
import { Login } from '../screens/Login';
import { LoginCode } from '../screens/LoginCode';
import { AuthLink } from '../screens/AuthLink';

const root = createRootRoute({ component: () => <Outlet /> });
const login = createRoute({ getParentRoute: () => root, path: '/login', component: Login });
const loginCode = createRoute({ getParentRoute: () => root, path: '/login/code', component: LoginCode });
const authLink = createRoute({ getParentRoute: () => root, path: '/auth/link', component: AuthLink });
/** Everything behind a session, the right books, and the Business plan. */
export const appLayout = createRoute({ getParentRoute: () => root, id: 'app', component: Gate });
const home = createRoute({ getParentRoute: () => appLayout, path: '/', component: lazyRouteComponent(() => import('../screens/Home'), 'Home') });

/** Records' filters, in the URL: back works, and a link shows the same list. */
export interface RecordsSearch {
  q?: string;
  from?: string;
  to?: string;
  dir?: 'in' | 'out';
  /** A category id from categories.list (only with a direction). */
  category?: string;
  check?: boolean;
  /** Open with the search box focused (the header's search). */
  find?: boolean;
  /** On a desktop, the entry open beside the list (spec 3A §6.6). */
  open?: string;
}

const yes = (v: unknown) => v === true || v === 'true';

export function readRecordsSearch(s: Record<string, unknown>): RecordsSearch {
  // The router reads ?q=6000 as a number: an amount search is still a search.
  const q = typeof s.q === 'string' ? s.q.trim() : typeof s.q === 'number' ? String(s.q) : '';
  return {
    ...(q ? { q: q.slice(0, 60) } : {}),
    ...(typeof s.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.from) ? { from: s.from } : {}),
    ...(typeof s.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.to) ? { to: s.to } : {}),
    ...(s.dir === 'in' || s.dir === 'out' ? { dir: s.dir } : {}),
    ...((s.dir === 'in' || s.dir === 'out') && typeof s.category === 'string' && /^[\w.-]{1,40}$/.test(s.category) ? { category: s.category } : {}),
    ...(yes(s.check) ? { check: true } : {}),
    ...(yes(s.find) ? { find: true } : {}),
    ...(typeof s.open === 'string' && /^[\w-]{1,64}$/.test(s.open) ? { open: s.open } : {}),
  };
}

const records = createRoute({
  getParentRoute: () => appLayout,
  path: '/records',
  validateSearch: readRecordsSearch,
  component: lazyRouteComponent(() => import('../screens/Records'), 'Records'),
});
const entry = createRoute({ getParentRoute: () => appLayout, path: '/records/$transactionId', component: lazyRouteComponent(() => import('../screens/Entry'), 'Entry') });
const alerts = createRoute({ getParentRoute: () => appLayout, path: '/alerts', component: lazyRouteComponent(() => import('../screens/Alerts'), 'Alerts') });
/** Customers' search and, on a desktop, the customer open beside the list (spec 3B §8.7). */
export interface CustomersSearch {
  q?: string;
  open?: string;
}

export function readCustomersSearch(s: Record<string, unknown>): CustomersSearch {
  const q = typeof s.q === 'string' ? s.q.trim() : typeof s.q === 'number' ? String(s.q) : '';
  return {
    ...(q ? { q: q.slice(0, 60) } : {}),
    ...(typeof s.open === 'string' && /^[\w-]{1,64}$/.test(s.open) ? { open: s.open } : {}),
  };
}

const customers = createRoute({
  getParentRoute: () => appLayout,
  path: '/customers',
  validateSearch: readCustomersSearch,
  component: lazyRouteComponent(() => import('../screens/Customers'), 'Customers'),
});
const customer = createRoute({ getParentRoute: () => appLayout, path: '/customers/$counterpartyId', component: lazyRouteComponent(() => import('../screens/Customer'), 'Customer') });
const settings = createRoute({ getParentRoute: () => appLayout, path: '/settings', component: lazyRouteComponent(() => import('../screens/Settings'), 'Settings') });
const more = createRoute({ getParentRoute: () => appLayout, path: '/more', component: lazyRouteComponent(() => import('../screens/More'), 'More') });
const captureShortcut = createRoute({ getParentRoute: () => appLayout, path: '/capture', component: lazyRouteComponent(() => import('../capture/CaptureShortcut'), 'CaptureShortcut') });

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree: root.addChildren([login, loginCode, authLink, appLayout.addChildren([home, records, entry, alerts, customers, customer, more, settings, captureShortcut])]),
    ...(history ? { history } : {}),
    defaultPreload: 'intent',
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
